import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { localeSchema, type Locale } from '@xfile/shared';
import { resolve, type MessageKey } from './catalogs';
import { loadCatalog, peekCatalog } from './catalog-loader';
import type { Messages } from './messages.fr';
import { detectInitialLocale, storeLocale } from './detect';

/** Fonction de traduction : chemin pointé → texte dans la langue courante. */
export type TFunction = (key: MessageKey) => string;

interface I18nContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: TFunction;
  /** Liste ordonnée des langues disponibles (pour le menu). */
  locales: readonly Locale[];
}

const I18nContext = createContext<I18nContextValue | null>(null);

/** Langues proposées, dans l'ordre d'affichage du menu. */
const AVAILABLE_LOCALES = localeSchema.options;

interface I18nProviderProps {
  children: React.ReactNode;
  /** Langue initiale forcée (tests) ; sinon détectée (localStorage → système → en). */
  initialLocale?: Locale;
}

export function I18nProvider({ children, initialLocale }: I18nProviderProps): React.JSX.Element {
  const [locale, setLocaleState] = useState<Locale>(() => initialLocale ?? detectInitialLocale());
  /**
   * Catalogue de la langue courante. `null` = pas encore chargé — situation qui n'existe PAS
   * en production (`main.tsx` résout le catalogue AVANT le premier rendu, donc aucun flash de
   * contenu non traduit) ni en test (`test-setup.ts` enregistre les trois catalogues). Le
   * repli sur la clé brute est un filet de sûreté, cohérent avec celui de `resolve`.
   */
  const [messages, setMessages] = useState<Messages | null>(() => peekCatalog(locale));

  /**
   * Langue VISÉE, tenue à jour de façon synchrone. Sert à ignorer un chargement retardataire :
   * fr → de → fr en deux clics rapides ferait sinon atterrir le catalogue allemand alors que
   * l'utilisateur est revenu au français.
   */
  const targetRef = useRef<Locale>(locale);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Reflète la langue sur <html lang> (a11y + SEO) à chaque changement.
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  // Filet : catalogue absent au montage (provider monté sans préchargement) → on le charge.
  useEffect(() => {
    if (peekCatalog(locale) !== null) return;
    void loadCatalog(locale).then((loaded) => {
      if (mountedRef.current && targetRef.current === locale) setMessages(loaded);
    });
  }, [locale]);

  const setLocale = useCallback((next: Locale) => {
    targetRef.current = next;
    storeLocale(next);
    setLocaleState(next);

    // Déjà chargé (retour à une langue vue plus tôt) : bascule SYNCHRONE, sans requête.
    const ready = peekCatalog(next);
    if (ready !== null) {
      setMessages(ready);
      return;
    }
    // Sinon on garde l'ANCIEN catalogue affiché le temps du chargement du nouveau : montrer
    // brièvement la langue précédente vaut mieux qu'un écran de clés brutes.
    void loadCatalog(next).then((loaded) => {
      if (mountedRef.current && targetRef.current === next) setMessages(loaded);
    });
  }, []);

  const t = useCallback<TFunction>(
    (key) => (messages === null ? key : resolve(messages, key)),
    [messages],
  );

  const value = useMemo<I18nContextValue>(
    () => ({ locale, setLocale, t, locales: AVAILABLE_LOCALES }),
    [locale, setLocale, t],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/** Accès au contexte i18n complet (langue courante, setter, `t`, langues). */
export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n doit être utilisé dans un <I18nProvider>');
  return ctx;
}
